/**
 * Is a pointer carrying a card over the board right now?
 *
 * The board's cells and cards answer a click by explaining themselves, and they
 * must not while a card is being carried across them: a release is a drop, not a
 * question. That used to be one `detailsEnabled` prop, derived at the top of
 * Board from all four planning modes and handed to every Cell — so a pickup and
 * a release each flipped one boolean on all 18 cells and all 18 cards. A value
 * prop that changes for EVERYONE is precisely what memo(Cell)/memo(BoardCard)
 * cannot absorb, and both of those boundaries land mid-gesture on a phone, where
 * re-reconciling the whole board is the hitch felt as the card sticking to the
 * finger.
 *
 * So the carry half of that flag travels as a stable box in context instead. The
 * context value's identity never changes, so no consumer re-renders when it
 * flips: handlers read `carrying` at the moment of the event, which is the moment
 * the answer is actually needed, and the two components that have something to
 * CLOSE when a carry begins subscribe with a listener rather than with a render.
 *
 * The other half — a selected hand card, an open targeting session — stays a
 * prop. Those are reachable from the keyboard, so they have to be visible in
 * role/tabIndex/aria-*, and unlike a carry they cannot begin mid-gesture.
 */
import { createContext, useContext } from 'react';

export interface BoardCarryGate {
  /**
   * True while a hand or staged card is being carried by a pointer. Read this
   * inside an event handler or a listener; reading it while rendering is a bug,
   * because nothing re-renders when it changes.
   */
  readonly carrying: boolean;
  /** Runs `listener` on every flip. Returns the unsubscribe. */
  subscribe: (listener: () => void) => () => void;
}

export interface BoardCarryController extends BoardCarryGate {
  set: (next: boolean) => void;
}

/** Board owns exactly one of these for its whole lifetime. */
export function createBoardCarryGate(): BoardCarryController {
  const listeners = new Set<() => void>();
  let carrying = false;
  return {
    get carrying(): boolean {
      return carrying;
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    set(next: boolean): void {
      if (carrying === next) return;
      carrying = next;
      // A copy, so a listener that unsubscribes itself (a card unmounting as the
      // carry starts) cannot cut the pass short for the cards after it.
      for (const listener of [...listeners]) listener();
    },
  };
}

/**
 * Outside a Board — lab previews, a Cell rendered directly by a test — nothing
 * is ever carried, so details always answer.
 */
const IDLE: BoardCarryGate = {
  carrying: false,
  subscribe: () => () => undefined,
};

export const BoardCarryContext = createContext<BoardCarryGate>(IDLE);

export function useBoardCarry(): BoardCarryGate {
  return useContext(BoardCarryContext);
}
